import { useMemo, useState } from "react";
import { getFirestore, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";

export const RootAdmin = () => {
  const db = getFirestore();

  const [form, setForm] = useState({
    classId: "",
    className: "",
    term: "",
    instructorEmail: "",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const isValidStanfordEmail = useMemo(() => {
    const v = form.instructorEmail.trim().toLowerCase();
    if (!v) return false;
    return /^[a-z0-9][a-z0-9._-]*@stanford\.edu$/.test(v);
  }, [form.instructorEmail]);

  const normalizedClassId = useMemo(() => {
    // Keep it simple and predictable: lowercase, trim.
    // You can also enforce a pattern here if you want (e.g. 5 chars).
    return form.classId.trim().toLowerCase();
  }, [form.classId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(null);

    try {
      setIsSaving(true);

      const classId = normalizedClassId;
      if (!classId) throw new Error("Class ID is required.");

      const email = form.instructorEmail.trim().toLowerCase();
      if (!email) throw new Error("Instructor email is required.");
      if (!isValidStanfordEmail) throw new Error("Instructor email must be an @stanford.edu address.");

      // 1) Get instructor UID from email (callable Cloud Function)
      const fn = httpsCallable(getFunctions(), "getUserIdFromEmail");
      const res = await fn({ email });
      const instructorUid = res?.data?.uid;

      if (!instructorUid) {
        throw new Error("No uid returned for that email.");
      }

      // 2) Prepare refs
      // Note: keeping your collection name as "class" per your prior code.
      const classRef = doc(db, "class", classId);
      const roleRef = doc(db, "users", instructorUid, "roles", classId);

      // 3) Atomic check + writes in one transaction
      await runTransaction(db, async (tx) => {
        const existing = await tx.get(classRef);
        if (existing.exists()) {
          throw new Error(`Class "${classId}" already exists.`);
        }

        tx.set(classRef, {
          classId, // handy to keep even though it's the doc id
          name: form.className.trim(),
          term: form.term.trim(),
          createdBy: {
            uid: instructorUid,
            email,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        tx.set(
          roleRef,
          {
            role: "admin",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      setSuccess(`Class created with id ${classId}`);
      setForm({ classId: "", className: "", term: "", instructorEmail: "" });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to create class.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card p-4">
            <h4 className="mb-3">Create New Class</h4>

            {error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : null}

            {success ? (
              <div className="alert alert-success" role="alert">
                {success}
              </div>
            ) : null}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Class ID</label>
                <input
                  type="text"
                  className="form-control"
                  name="classId"
                  placeholder="e.g. cs109w or 123ab"
                  value={form.classId}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                />
                <div className="form-text">
                  This will be used as the Firestore document id. Must be unique.
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Class Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="className"
                  placeholder="e.g. CS109: Probability for Computer Scientists"
                  value={form.className}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Term</label>
                <input
                  type="text"
                  className="form-control"
                  name="term"
                  placeholder="e.g. Winter 26"
                  value={form.term}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Instructor Stanford Email (sunet id email)</label>
                <input
                  type="email"
                  className={`form-control ${
                    form.instructorEmail && !isValidStanfordEmail ? "is-invalid" : ""
                  }`}
                  name="instructorEmail"
                  placeholder="e.g. cpiech@stanford.edu"
                  value={form.instructorEmail}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                />
                <div className="form-text">Must be an @stanford.edu address.</div>
              </div>

              <button type="submit" className="btn btn-primary w-100" disabled={isSaving}>
                {isSaving ? "Creating..." : "Create Class"}
              </button>
            </form>

            {/* Optional: show what class id will normalize to */}
            {form.classId ? (
              <div className="mt-3 small text-muted">
                Normalized Class ID: <code>{normalizedClassId}</code>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};